'use client';

import {
	ENUM_UTILS,
	MarketId,
	MarketKey,
	UISerializableOrder,
	OrderBookBidAsk,
	BidsAndAsks,
} from '@velocity-exchange/common';
import { BigNum, OrderType, PositionDirection } from '@velocity-exchange/sdk';
import { useCallback, useEffect, useState } from 'react';
import useVelocityStore from '../stores/VelocityStore/useVelocityStore';
import useGetOraclePriceForMarket from 'src/hooks/useGetOraclePriceForMarket';
import { useCurrentAccountOrders } from './query/accountTradingData/useOrders';

export const DEFAULT_USER_BIDS_AND_ASKS: BidsAndAsks = {
	bids: [],
	asks: [],
};

type AllMarketsUserBidsAndAsks = Record<MarketKey, BidsAndAsks>;

interface OrderBookBidAskWithMarket extends OrderBookBidAsk {
	marketKey: MarketKey;
}

const orderToBidAsk = (
	order: UISerializableOrder,
	orderOraclePrice: BigNum
): OrderBookBidAskWithMarket => {
	const marketId = new MarketId(order.marketIndex, order.marketType);

	let price = order.price.toNum();

	// For limit orders with oracle offset, use oracle price + offset
	if (
		ENUM_UTILS.match(order.orderType, OrderType.LIMIT) &&
		order.oraclePriceOffset &&
		!order.oraclePriceOffset.eqZero() &&
		orderOraclePrice
	) {
		price = orderOraclePrice.add(order.oraclePriceOffset).toNum();
	}

	return {
		price,
		size: order.baseAssetAmount.sub(order.baseAssetAmountFilled).toNum(),
		type: 'dlob',
		marketKey: marketId.key,
	};
};

export function allOrdersToUserBidsAndAsks(
	ordersToConvert: UISerializableOrder[],
	currentOraclePrice: BigNum
): AllMarketsUserBidsAndAsks {
	const bids = ordersToConvert
		.filter(
			(order) =>
				ENUM_UTILS.match(order.direction, PositionDirection.LONG) &&
				!ENUM_UTILS.match(order.orderType, OrderType.MARKET)
		)
		.map((order) => orderToBidAsk(order, currentOraclePrice));

	const asks = ordersToConvert
		.filter(
			(order) =>
				ENUM_UTILS.match(order.direction, PositionDirection.SHORT) &&
				!ENUM_UTILS.match(order.orderType, OrderType.MARKET)
		)
		.map((order) => orderToBidAsk(order, currentOraclePrice));

	const allMarketsWithUserOrders = Array.from(
		new Set(bids.map((b) => b.marketKey).concat(asks.map((a) => a.marketKey)))
	);

	const bidsAndAsksByMarket: AllMarketsUserBidsAndAsks = {};

	allMarketsWithUserOrders.forEach((marketKey) => {
		const bidsForCurrentMarket = bids.filter((b) => b.marketKey === marketKey);
		const asksForCurrentMarket = asks.filter((a) => a.marketKey === marketKey);

		bidsAndAsksByMarket[marketKey] = {
			bids: bidsForCurrentMarket,
			asks: asksForCurrentMarket,
		};
	});

	return bidsAndAsksByMarket;
}

const useCurrentUserBidsAndAsks = (): {
	getUserBidsAndAsksForMarket: (marketKey: MarketKey) => BidsAndAsks;
} => {
	const currentMarketId = useVelocityStore(
		(s) => s.selectedMarket.current?.marketId
	);
	const allOrders = useCurrentAccountOrders();

	const [allMarketsUserBidsAndAsks, setAllMarketsUserBidsAndAsks] = useState<
		Record<MarketKey, BidsAndAsks>
	>({});

	const getOraclePrice = useGetOraclePriceForMarket();
	const [oraclePrice, setOraclePrice] = useState<BigNum | undefined>(
		getOraclePrice(currentMarketId)
	);

	useEffect(() => {
		// Oracle Price affects the price of oracle offset orders (because they are relative to the oracle price), so we need to trigger them to update when the oracle price updates - not only when the state of the orders changes. Make the oracle price update every second to avoid unnecessary computation when the oracle is ticking quickly.
		const interval = setInterval(() => {
			setOraclePrice(getOraclePrice(currentMarketId));
		}, 1000);
		return () => clearInterval(interval);
	}, [currentMarketId, getOraclePrice]);

	useEffect(() => {
		const computedBidsAndAsks = allOrdersToUserBidsAndAsks(
			allOrders,
			oraclePrice as BigNum
		);
		setAllMarketsUserBidsAndAsks(computedBidsAndAsks);
	}, [allOrders, oraclePrice]);

	const getUserBidsAndAsksForMarket = useCallback(
		(marketKey: MarketKey) => {
			return allMarketsUserBidsAndAsks[marketKey] ?? DEFAULT_USER_BIDS_AND_ASKS;
		},
		[allMarketsUserBidsAndAsks]
	);

	return {
		getUserBidsAndAsksForMarket,
	};
};

export default useCurrentUserBidsAndAsks;
